import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useMemo, useState } from 'react';

import { TRAFFIC_TYPES, useTrafficVisitBreakdown } from '@/api/common';
import Flare, { Donut, Legend, Tooltip } from '@/components/Flare';
import { getPeriodTime, usePageFilterContext } from '@/components/page';
import { Panel } from '@/components/panels';
import {
  AUDIENCE_PRIMARY,
  BOT_PRIMARY,
  TAM_PRIMARY,
  UNKNOWN_PRIMARY,
  VISITS_PRIMARY,
} from '@/constants';
import { numberFormat } from '@/helper/numberFormatter';
import { ROUTES } from '@/router';
import { COLOR_WHITE } from '@/styles/palette';

type Props = {
  channelId?: string;
  vendorId?: string;
  campaignIds?: string[];
  companyId?: string;
  groupBy?: 'traffic_type' | 'revrange' | 'sector';
  isLoading?: boolean;
};

const VisitEfficiencyDonutChart = ({
  channelId,
  vendorId,
  campaignIds,
  companyId,
  groupBy = 'traffic_type',
  isLoading: isVendorLoading,
}: Props) => {
  const {
    pageFilters: { startDate, endDate, audience },
  } = usePageFilterContext();

  const {
    trafficVisitBreakdown: data,
    isLoading: isTrafficVisitBreakdownLoading,
    error,
  } = useTrafficVisitBreakdown({
    startDate,
    endDate,
    audienceIds: audience?.isTAM || !audience?.id ? undefined : [audience.id],
    channelId,
    vendorId,
    campaignIds,
    companyId,
    groupBy,
  });

  const [disabledSeries, setDisabledSeries] = useState<string[] | undefined>([
    TRAFFIC_TYPES.UNRESOLVED,
  ]);

  const isLoading = isVendorLoading || isTrafficVisitBreakdownLoading;
  const titleValue = audience?.isTAM ? t`TAM<br/>Visits` : t`Target Audience<br/>Visits`;

  const totalEnabledVisits = useMemo(() => {
    let total = 0;

    if (data && data.length > 0) {
      if (!disabledSeries?.includes(TRAFFIC_TYPES.TARGET)) {
        total += data[0].targetVisits;
      }
      if (!disabledSeries?.includes(TRAFFIC_TYPES.OTHER)) {
        total += data[0].otherVisits;
      }
      if (!disabledSeries?.includes(TRAFFIC_TYPES.BOT)) {
        total += data[0].botVisits;
      }
      if (!disabledSeries?.includes(TRAFFIC_TYPES.UNRESOLVED)) {
        total += data[0].unresolvedVisits;
      }
    }
    return total;
  }, [data, disabledSeries]);

  const seriesData = useMemo(() => {
    if (!data) {
      return undefined;
    }

    return [
      {
        id: 'visits',
        name: t`Visits`,
        data: [
          {
            id: TRAFFIC_TYPES.TARGET,
            value: data[0].targetVisits,
            name: audience?.isTAM ? t`TAM` : (audience?.name ?? t`Audience`),
            color: audience?.isTAM ? TAM_PRIMARY : AUDIENCE_PRIMARY,
            custom: { secondary: data[0].targetVisits / totalEnabledVisits },
          },
          {
            id: TRAFFIC_TYPES.OTHER,
            value: data[0].otherVisits,
            name: t`Other Companies`,
            color: VISITS_PRIMARY,
            custom: { secondary: data[0].otherVisits / totalEnabledVisits },
          },
          {
            id: TRAFFIC_TYPES.BOT,
            value: data[0].botVisits,
            name: t`Bot`,
            color: BOT_PRIMARY,
            custom: { secondary: data[0].botVisits / totalEnabledVisits },
          },
          {
            id: TRAFFIC_TYPES.UNRESOLVED,
            value: data[0].unresolvedVisits,
            name: t`Unknown`,
            color: UNKNOWN_PRIMARY,
            custom: {
              secondary: data[0].unresolvedVisits / totalEnabledVisits,
            },
          },
        ],
      },
    ];
  }, [data, audience?.isTAM, audience?.name, totalEnabledVisits]);

  return (
    <Panel title={<Trans>Visit Efficiency</Trans>} style={{ height: 483 }} noPadding>
      <Flare
        data={seriesData}
        title={`<b>${numberFormat(data?.[0].targetVisits)}</b><br />${titleValue}`}
        width="auto"
        height="auto"
        colors={seriesData ? seriesData?.[0].data.map((d) => d.color) : []}
        noPadding
        isLoading={isLoading}
        error={error}
        disabledSeriesIds={disabledSeries}
        onDisabledSeriesChange={setDisabledSeries}
      >
        <Legend showByCategory />
        <Donut
          y="value"
          noBorder
          noHoverEffect
          dataLabelColor={COLOR_WHITE}
          dataLabelFormat={(label) => {
            if (label.custom?.secondary && label.custom.secondary >= 0.02) {
              return numberFormat(label.custom?.secondary, { isPercent: true, precision: 0 });
            }
            return '';
          }}
          hideConnector
        />
        <Tooltip
          titleFormat={() => t`${getPeriodTime(startDate, endDate)} (Visits)`}
          rowLabelFormat={(item) => item?.key}
          rowValueFormat={(item) => {
            return numberFormat(item?.y);
          }}
          rowSecondaryValueFormat={(item) => {
            if (item?.custom?.secondary && item.custom.secondary > 0) {
              return numberFormat(item?.custom.secondary, { isPercent: true, precision: 1 });
            }
          }}
          rowValueLink={(point) => {
            if (point?.id === TRAFFIC_TYPES.TARGET) {
              return ROUTES.webTrafficActivityWithParams({
                'w.vendorIds': vendorId,
                'w.audienceIds': audience?.isTAM ? undefined : audience?.id,
                'w.inTam': audience?.isTAM ? 'true' : undefined,
                'w.metrics.visits': '[1,]',
              });
            }
            if (point?.id === TRAFFIC_TYPES.OTHER) {
              return ROUTES.webTrafficActivityWithParams({
                'w.vendorIds': vendorId,
                'w.audienceIds!': audience?.isTAM ? undefined : audience?.id,
                'w.inTam': audience?.isTAM ? 'false' : undefined,
                'w.metrics.visits': '[1,]',
              });
            }
            if (point?.key === 'total') {
              return ROUTES.webTrafficActivityWithParams({
                'w.vendorIds': vendorId,
                'w.metrics.visits': '[1,]',
              });
            }
          }}
          showTotalRow
          unstable_hasRouterContext={false}
          shared
        />
      </Flare>
    </Panel>
  );
};

export default VisitEfficiencyDonutChart;
